import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    Paper,
    Divider,
    Avatar,
    Fade,
    useTheme
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../services/api';
import OfferModal from '../components/marketing/OfferModal';
import SubscriptionModal from '../components/marketing/SubscriptionModal';
import ContractModal from '../components/marketing/ContractModal';
import { useToast } from '../context/ToastContext';
import ConfirmDialog from '../components/common/ConfirmDialog';

const MarketingPage = () => {
    const theme = useTheme();
    const [offers, setOffers] = useState<any[]>([]);
    const [subscriptions, setSubscriptions] = useState<any[]>([]);
    const [open, setOpen] = useState(false);
    const [editingOffer, setEditingOffer] = useState<any | null>(null);
    const [subModalOpen, setSubModalOpen] = useState(false);
    const [selectedOfferForSub, setSelectedOfferForSub] = useState<{ id: string, title: string } | null>(null);
    const [contractModalOpen, setContractModalOpen] = useState(false);
    const [selectedOrgForContract, setSelectedOrgForContract] = useState<{ id: string, name: string } | null>(null);
    const [selectedOfferForContract, setSelectedOfferForContract] = useState<any | null>(null);
    const { showToast } = useToast();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [offerToDelete, setOfferToDelete] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchOffers();
        fetchSubscriptions();
    }, []);

    const fetchOffers = async () => {
        try {
            setLoading(true);
            const res = await api.get('/api/offer');
            setOffers(res.data);
        } catch (err) {
            console.error("Error fetching offers", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSubscriptions = async () => {
        try {
            const res = await api.get('/api/subscription');
            setSubscriptions(res.data);
            console.log(res.data);
        } catch (err) {
            console.error("Error fetching subscriptions", err);
        }
    };

    const handleCreate = async (offerData: any) => {
        try {
            await api.post('/api/offer', offerData);
            setOpen(false);
            fetchOffers();
            showToast('Offre créée avec succès', 'success');
        } catch (err) {
            console.error("Error creating offer", err);
            showToast('Erreur lors de la création de l\'offre', 'error');
        }
    };

    const handleUpdate = async (offerData: any) => {
        try {
            await api.put(`/api/offer/${editingOffer._id}`, offerData);
            setOpen(false);
            setEditingOffer(null);
            fetchOffers();
            showToast('Offre mise à jour avec succès', 'success');
        } catch (err) {
            console.error("Error updating offer", err);
            showToast('Erreur lors de la mise à jour de l\'offre', 'error');
        }
    };

    const handleDeleteClick = (id: string) => {
        setOfferToDelete(id);
        setConfirmOpen(true);
    };

    const confirmDeleteOffer = async () => {
        if (!offerToDelete) return;
        try {
            await api.delete(`/api/offer/${offerToDelete}`);
            fetchOffers();
            showToast('Offre supprimée avec succès', 'success');
        } catch (err) {
            console.error("Error deleting offer", err);
            showToast('Erreur lors de la suppression de l\'offre', 'error');
        } finally {
            setConfirmOpen(false);
            setOfferToDelete(null);
        }
    };

    const handleOpenModal = (offer: any = null) => {
        setEditingOffer(offer);
        setOpen(true);
    };

    const handleSubscribeClick = (offer: any) => {
        setSelectedOfferForSub({ id: offer._id, title: offer.title });
        setSubModalOpen(true);
    };

    const handleSubscriptionSuccess = () => {
        fetchSubscriptions();
        setSubModalOpen(false);
    };

    const handleOpenContractModal = (org: any, offer: any) => {
        console.log("Opening contract modal for org:", org, "with offer:", offer);
        if (!org) {
            showToast("Erreur: organisation non trouvée", "error");
            return;
        }
        setSelectedOrgForContract({ id: org._id, name: org.name });
        setSelectedOfferForContract(offer);
        setContractModalOpen(true);
    };

    // Calculate statistics
    const activeOffers = offers.filter(o => o.isActive).length;
    const totalParcours = offers.reduce((acc, o) => acc + (o.parcours?.length || 0), 0);
    const avgPrice = offers.length > 0
        ? Math.round(offers.reduce((acc, o) => acc + (o.discountPrice || o.price), 0) / offers.length)
        : 0;

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{
                    position: 'absolute',
                    top: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.1)',
                }} />
                <Box sx={{
                    position: 'absolute',
                    bottom: -30,
                    right: 100,
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{
                                bgcolor: 'rgba(255,255,255,0.2)',
                                width: 56,
                                height: 56,
                                backdropFilter: 'blur(10px)'
                            }}>
                                <LocalOfferIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>
                                    Marketing & Offres
                                </Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>
                                    Gérez vos offres commerciales et packages de formation
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal(null)}
                            sx={{
                                mt: 2,
                                bgcolor: 'white',
                                color: '#667eea',
                                fontWeight: 700,
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '1rem',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.95)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            Créer une Offre
                        </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{offers.length}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Offres</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{activeOffers}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Actives</Typography>
                                </Box>
                            </Grid>
                            <Grid size={{ xs: 4 }}>
                                <Box sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: 3,
                                    p: 2,
                                    textAlign: 'center',
                                }}>
                                    <Typography variant="h3" sx={{ fontWeight: 700 }}>{totalParcours}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Parcours</Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            {/* Empty State */}
            {!loading && offers.length === 0 && (
                <Fade in timeout={500}>
                    <Paper
                        sx={{
                            p: 6,
                            textAlign: 'center',
                            borderRadius: 4,
                            background: 'white',
                            border: '2px dashed #e0e0e0',
                        }}
                    >
                        <LocalOfferIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h5" sx={{ fontWeight: 600, color: '#666', mb: 1 }}>
                            Aucune offre pour le moment
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 3 }}>
                            Créez votre première offre pour commencer à commercialiser vos formations
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenModal(null)}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontWeight: 600,
                                px: 4,
                                py: 1.5,
                                borderRadius: 3,
                                textTransform: 'none',
                            }}
                        >
                            Créer ma première offre
                        </Button>
                    </Paper>
                </Fade>
            )}

            {/* Offers Grid */}
            <Grid container spacing={3}>
                {offers.map((offer, index) => (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={offer._id}>
                        <Fade in timeout={300 + index * 100}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    borderRadius: 4,
                                    overflow: 'visible',
                                    position: 'relative',
                                    transition: 'all 0.3s ease',
                                    border: '1px solid',
                                    borderColor: offer.isActive ? 'rgba(102, 126, 234, 0.2)' : 'rgba(0,0,0,0.08)',
                                    boxShadow: offer.isActive
                                        ? '0 4px 20px rgba(102, 126, 234, 0.15)'
                                        : '0 2px 12px rgba(0,0,0,0.08)',
                                    '&:hover': {
                                        transform: 'translateY(-8px)',
                                        boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
                                    },
                                    opacity: offer.isActive ? 1 : 0.7,
                                }}
                            >
                                {/* Status Badge */}
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: -12,
                                        right: 16,
                                        zIndex: 1,
                                    }}
                                >
                                    <Chip
                                        icon={offer.isActive ? <CheckCircleIcon /> : <VisibilityOffIcon />}
                                        label={offer.isActive ? 'Actif' : 'Inactif'}
                                        size="small"
                                        sx={{
                                            bgcolor: offer.isActive ? '#4caf50' : '#9e9e9e',
                                            color: 'white',
                                            fontWeight: 600,
                                            px: 1,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                            '& .MuiChip-icon': { color: 'white' },
                                        }}
                                    />
                                </Box>

                                {/* Price Header */}
                                <Box
                                    sx={{
                                        background: offer.isActive
                                            ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                                            : 'linear-gradient(135deg, #9e9e9e 0%, #757575 100%)',
                                        color: 'white',
                                        p: 3,
                                        pt: 4,
                                        textAlign: 'center',
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, opacity: 0.95 }}>
                                        {offer.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1 }}>
                                        <Typography variant="h3" sx={{ fontWeight: 700 }}>
                                            {offer.discountPrice || offer.price}€
                                        </Typography>
                                        {offer.discountPrice && (
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    textDecoration: 'line-through',
                                                    opacity: 0.7,
                                                }}
                                            >
                                                {offer.price}€
                                            </Typography>
                                        )}
                                    </Box>
                                    {offer.monthlyPrice > 0 && (
                                        <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                            ou {offer.monthlyPrice}€/mois
                                        </Typography>
                                    )}
                                    {offer.discountPrice && offer.price > 0 && (
                                        <Chip
                                            icon={<TrendingUpIcon />}
                                            label={`-${Math.round((1 - offer.discountPrice / offer.price) * 100)}%`}
                                            size="small"
                                            sx={{
                                                mt: 1.5,
                                                bgcolor: 'rgba(255,255,255,0.25)',
                                                color: 'white',
                                                fontWeight: 700,
                                                '& .MuiChip-icon': { color: 'white' },
                                            }}
                                        />
                                    )}
                                </Box>

                                {/* Content */}
                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 3,
                                            minHeight: 48,
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        {offer.description || 'Aucune description'}
                                    </Typography>

                                    <Divider sx={{ mb: 2 }} />

                                    {/* Included Parcours */}
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#667eea',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        <SchoolIcon fontSize="small" />
                                        Parcours inclus ({offer.parcours?.length || 0})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {offer.parcours?.slice(0, 3).map((p: any) => (
                                            <Chip
                                                key={p._id}
                                                label={p.title}
                                                size="small"
                                                variant="outlined"
                                                sx={{
                                                    borderColor: '#667eea',
                                                    color: '#667eea',
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        ))}
                                        {offer.parcours?.length > 3 && (
                                            <Chip
                                                label={`+${offer.parcours.length - 3}`}
                                                size="small"
                                                sx={{
                                                    bgcolor: '#f0f0f0',
                                                    fontSize: '0.75rem',
                                                }}
                                            />
                                        )}
                                        {(!offer.parcours || offer.parcours.length === 0) && (
                                            <Typography variant="caption" color="text.secondary">
                                                Aucun parcours
                                            </Typography>
                                        )}
                                    </Box>

                                    <Divider sx={{ mb: 2, mt: 2 }} />

                                    {/* Assigned Organizations */}
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#667eea',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: 1.5,
                                        }}
                                    >
                                        <BusinessIcon fontSize="small" />
                                        Organisations assignées ({subscriptions.filter(s => s.offer?._id === offer._id || s.offer === offer._id).length})
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {subscriptions.filter(s => s.offer?._id === offer._id || s.offer === offer._id).map((sub) => (
                                            <Chip
                                                key={sub._id}
                                                label={sub.organization?.name || 'Org inconnue'}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleOpenContractModal(sub.organization, offer)}
                                                sx={{
                                                    borderColor: '#48bb78',
                                                    color: '#2f855a',
                                                    fontSize: '0.75rem',
                                                    bgcolor: 'rgba(72, 187, 120, 0.1)',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(72, 187, 120, 0.2)',
                                                    }
                                                }}
                                            />
                                        ))}
                                        {subscriptions.filter(s => s.offer?._id === offer._id || s.offer === offer._id).length === 0 && (
                                            <Typography variant="caption" color="text.secondary">
                                                Aucune organisation assignée
                                            </Typography>
                                        )}
                                    </Box>
                                </CardContent>

                                {/* Actions */}
                                <Box
                                    sx={{
                                        p: 2,
                                        pt: 0,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderTop: '1px solid',
                                        borderColor: 'divider',
                                        mt: 'auto',
                                    }}
                                >
                                    <Box>
                                        <Tooltip title="Modifier">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleOpenModal(offer)}
                                                sx={{
                                                    color: '#667eea',
                                                    '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)' },
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Supprimer">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(offer._id)}
                                                sx={{
                                                    color: '#f44336',
                                                    '&:hover': { bgcolor: 'rgba(244, 67, 54, 0.1)' },
                                                }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<PersonAddIcon />}
                                        onClick={() => handleSubscribeClick(offer)}
                                        sx={{
                                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                            },
                                        }}
                                    >
                                        Assigner
                                    </Button>
                                </Box>
                            </Card>
                        </Fade>
                    </Grid>
                ))}
            </Grid>

            {/* Modals */}
            <OfferModal
                open={open}
                onClose={() => setOpen(false)}
                onSave={editingOffer ? handleUpdate : handleCreate}
                initialData={editingOffer}
                isEditing={!!editingOffer}
            />

            <SubscriptionModal
                open={subModalOpen}
                onClose={() => setSubModalOpen(false)}
                offerId={selectedOfferForSub?.id || null}
                offerTitle={selectedOfferForSub?.title || null}
                onSuccess={handleSubscriptionSuccess}
            />

            <ContractModal
                open={contractModalOpen}
                onClose={() => setContractModalOpen(false)}
                organizationId={selectedOrgForContract?.id || null}
                organizationName={selectedOrgForContract?.name || null}
                offer={selectedOfferForContract}
            />
            <ConfirmDialog
                open={confirmOpen}
                title="Supprimer l'offre"
                content="Êtes-vous sûr de vouloir supprimer cette offre ? Cette action est irréversible."
                onConfirm={confirmDeleteOffer}
                onCancel={() => setConfirmOpen(false)}
            />
        </Box>
    );
};

export default MarketingPage;
